<script lang="ts">
	import type { PageData } from './$types';
	import Button from '$lib/components/primitives/Button.svelte';

	interface Props {
		data: PageData;
	}

	let { data }: Props = $props();

	function formatDate(dateString: string, locale: string): string {
		const date = new Date(dateString);
		return date.toLocaleDateString(locale, {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	}
</script>

<svelte:head>
	<title>{data.story.customer}: {data.story.title} | Heyweek</title>
	<meta name="description" content={data.meta.description} />
	<link rel="canonical" href="https://heyweek.com{data.meta.canonical}" />
	<meta property="og:title" content="{data.story.customer}: {data.story.title} | Heyweek" />
	<meta property="og:description" content={data.meta.description} />
	<meta property="og:type" content="article" />
	<meta property="article:published_time" content={data.meta.publishedTime} />
</svelte:head>

<main class="page">
	<!-- Hero -->
	<section class="hero">
		<!-- Breadcrumb sits above the banner -->
		<div class="hero__top">
			<nav class="breadcrumb">
				<a href="/customers">← Customer stories</a>
			</nav>
		</div>

		<!-- Full-width banner -->
		{#if data.story.heroBanner}
			<div class="hero__banner">
				<img src={data.story.heroBanner} alt={data.story.customer} />
			</div>
		{/if}

		<!-- Title block below banner -->
		<div class="hero__body">
			<h1 class="hero__title">{data.story.title}</h1>
			<p class="hero__excerpt">{data.story.excerpt}</p>
		</div>
	</section>

	<!-- Article -->
	<div class="container">
		<!-- Fact row -->
		<dl class="hero__facts">
			<div class="hero__fact">
				<dt>Company</dt>
				<dd>
					{#if data.story.website}
						<a
							href={data.story.website}
							target="_blank"
							rel="noopener noreferrer"
							class="hero__outlink"
						>
							{data.story.customer}<svg
								width="11"
								height="11"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2.5"
								stroke-linecap="round"
								stroke-linejoin="round"
								aria-hidden="true"
								><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline
									points="15 3 21 3 21 9"
								/><line x1="10" y1="14" x2="21" y2="3" /></svg
							>
						</a>
					{:else}
						{data.story.customer}
					{/if}
				</dd>
			</div>
			{#if data.story.industry}
				<div class="hero__fact">
					<dt>Industry</dt>
					<dd>{data.story.industry}</dd>
				</div>
			{/if}
			{#if data.story.founded}
				<div class="hero__fact">
					<dt>Founded</dt>
					<dd>{data.story.founded}</dd>
				</div>
			{/if}
		</dl>

		<div class="content">
			{@html data.story.content}
		</div>

		<!-- Footer -->
		<footer class="footer">
			<p>See how Heyweek can work for your team.</p>
			<div class="footer__actions">
				<Button href="https://app.heyweek.com/?signup" variant="primary" size="md"
					>Start free</Button
				>
				<a href="/customers" class="footer__back">← Back to customer stories</a>
			</div>
		</footer>
	</div>
</main>

<style>
	.page {
		min-height: 100vh;
	}

	/* ── Hero ── */
	.hero {
		border-bottom: 1px solid var(--border-light);
	}

	.hero__top {
		max-width: 800px;
		margin-inline: auto;
		padding-inline: var(--container-padding, 1.5rem);
		padding-block: var(--space-10);
	}

	.breadcrumb a {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		text-decoration: none;
	}

	.breadcrumb a:hover {
		color: var(--text-primary);
	}

	.hero__banner {
		max-width: 800px;
		margin-inline: auto;
		padding-inline: var(--container-padding, 1.5rem);
		border-radius: var(--radius-xl);
		overflow: hidden;
	}

	.hero__banner img {
		width: 100%;
		height: auto;
		display: block;
		border-radius: var(--radius-xl);
	}

	.hero__body {
		max-width: 800px;
		margin-inline: auto;
		padding-inline: var(--container-padding, 1.5rem);
		padding-block: var(--space-10) var(--space-12);
	}

	.hero__facts {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-10);
		margin-bottom: var(--space-10);
		padding: 0;
		justify-content: center;
	}

	.hero__fact {
		display: flex;
		flex-direction: column;
		gap: 2px;
		align-items: center;
		text-align: center;
	}

	.hero__fact dt {
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-medium);
		color: var(--text-secondary);
		text-transform: uppercase;
		letter-spacing: 0.06em;
	}

	.hero__fact dd {
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0;
	}

	.hero__outlink {
		display: inline-flex;
		align-items: center;
		gap: 3px;
		color: var(--text-primary);
		text-decoration: none;
	}

	.hero__outlink:hover {
		color: var(--text-brand);
	}

	.hero__outlink svg {
		opacity: 0.5;
		flex-shrink: 0;
		position: relative;
		top: -1px;
	}

	.hero__title {
		font-size: clamp(2rem, 5vw, 3.25rem);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		letter-spacing: -0.03em;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
	}

	.hero__excerpt {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.7;
		max-width: 600px;
		hyphens: none;
	}

	/* ── Article ── */
	.container {
		max-width: 720px;
		margin-inline: auto;
		padding-inline: var(--container-padding, 1.5rem);
		padding-block: var(--space-10) var(--space-20);
	}

	.content {
		font-size: var(--font-size-lg);
		line-height: 1.8;
		color: var(--text-primary);
	}

	.content :global(h1) {
		display: none;
	}

	.content :global(h2) {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		letter-spacing: -0.02em;
		color: var(--text-primary);
		margin-top: var(--space-12);
		margin-bottom: var(--space-4);
		line-height: 1.3;
	}

	.content :global(h3) {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-top: var(--space-8);
		margin-bottom: var(--space-3);
	}

	.content :global(p) {
		margin-bottom: var(--space-5);
		hyphens: none;
	}

	.content :global(p strong) {
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
	}

	.content :global(blockquote) {
		margin-block: var(--space-8);
		margin-inline: 0;
		padding-left: var(--space-6);
		border-left: 2px solid var(--text-brand);
	}

	.content :global(blockquote p) {
		font-size: var(--font-size-lg);
		font-style: italic;
		color: var(--text-primary);
		margin-bottom: 0;
		line-height: 1.65;
	}

	.content :global(hr) {
		border: none;
		border-top: 1px solid var(--border-light);
		margin-block: var(--space-10);
	}

	.content :global(a) {
		color: var(--text-brand);
		text-decoration: underline;
		text-underline-offset: 3px;
	}

	.content :global(em) {
		font-style: italic;
	}

	/* Footnote — italic paragraph after <hr> */
	.content :global(hr + p) {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: 1.7;
	}

	.content :global(hr + p em) {
		font-style: normal;
	}

	/* ── Footer ── */
	.footer {
		margin-top: var(--space-16);
		padding-top: var(--space-10);
		border-top: 1px solid var(--border-light);
	}

	.footer p {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-medium);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
	}

	.footer__actions {
		display: flex;
		align-items: center;
		gap: var(--space-6);
		flex-wrap: wrap;
	}

	.footer__back {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		text-decoration: none;
	}

	.footer__back:hover {
		color: var(--text-primary);
	}
</style>
